import { useState, useEffect, useRef, useCallback } from 'react';
import authService from '../services/authService';

/**
 * Custom hook for real-time import progress and data streaming via SSE
 * Enhances existing progress callback mechanism with real-time updates
 */
const useImportStream = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [newRecords, setNewRecords] = useState([]);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const [importStatus, setImportStatus] = useState(null); // 'importing', 'completed', 'error'
  
  const eventSourceRef = useRef(null);
  const activeImportRef = useRef(null);
  const newRecordsCallbackRef = useRef(null);
  const progressCallbackRef = useRef(null);
  const importCompletedRef = useRef(false);

  /**
   * Connect to SSE stream for a specific import
   * @param {string} franchiseeId - The franchisee ID
   * @param {string} importType - 'customers' or 'invoices'
   * @param {function} progressCallback - Optional progress callback for backward compatibility
   * @param {function} newRecordsCallback - Callback for new records (page1 only)
   */
  const connectToImportStream = useCallback((franchiseeId, importType, progressCallback, newRecordsCallback) => {
    // Clean up existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    // Store callbacks and reset completion flag
    progressCallbackRef.current = progressCallback;
    newRecordsCallbackRef.current = newRecordsCallback;
    activeImportRef.current = { franchiseeId, importType };
    importCompletedRef.current = false;

    try {
      const token = authService.getToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      // Create SSE connection with auth header via URL params (SSE doesn't support custom headers directly)
      // Handle both URL formats: with and without /api suffix
      const envUrl = process.env.REACT_APP_API_URL || 'http://localhost:9003/api';
      const baseUrl = envUrl.endsWith('/api') ? envUrl : `${envUrl}/api`;
      const url = `${baseUrl}/import/stream/${franchiseeId}/${importType}?token=${encodeURIComponent(token)}`;
      
      console.log('📡 Connecting to import stream:', url);
      
      const eventSource = new EventSource(url);
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        console.log('📡 SSE connection opened');
        setIsConnected(true);
        setConnectionError(null);
        setImportStatus('importing');
      };

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('📡 SSE message received:', data.type, data);

          switch (data.type) {
            case 'connected':
              console.log('📡 SSE connection confirmed:', data.sessionId);
              break;

            case 'heartbeat':
              // Silent heartbeat - keeps connection alive
              break;

            case 'progress':
              // Update progress state
              setImportProgress({
                current: data.current || 0,
                total: data.total || 0
              });

              // Call existing progress callback for backward compatibility
              if (progressCallbackRef.current) {
                progressCallbackRef.current(data.current || 0, data.total || 0);
              }
              break;

            case 'records':
              // Handle new records (for real-time list updates)
              if (data.records && data.records.length > 0) {
                console.log(`📡 Received ${data.records.length} new ${importType}:`, data.records);
                
                setNewRecords(prev => [...prev, ...data.records]);
                
                // Call new records callback for real-time UI updates
                if (newRecordsCallbackRef.current) {
                  newRecordsCallbackRef.current(data.records, data);
                }
              }
              break;

            case 'completed':
              console.log('📡 Import completed:', data);
              setImportStatus('completed');
              importCompletedRef.current = true; // Set completion flag immediately
              
              // Final progress update
              setImportProgress({
                current: data.total || 0,
                total: data.total || 0
              });

              // Call final progress callback
              if (progressCallbackRef.current) {
                progressCallbackRef.current(data.total || 0, data.total || 0);
              }

              // Auto-disconnect after completion
              setTimeout(() => {
                disconnectFromImportStream();
              }, 2000);
              break;

            default:
              console.log('📡 Unknown SSE message type:', data.type);
          }
        } catch (error) {
          console.error('📡 Error parsing SSE message:', error, event.data);
        }
      };

      eventSource.onerror = (error) => {
        // Check if this is normal closure first, before logging as error
        if (eventSource.readyState === EventSource.CLOSED || importCompletedRef.current) {
          console.log('📡 SSE connection closed normally (after completion)');
          setIsConnected(false);
          return;
        }
        
        // Only log as error for actual errors
        console.error('📡 SSE connection error:', error);
        console.log('📡 Actual SSE connection error - setting error state');
        setConnectionError('Connection to import stream failed');
        setIsConnected(false);
        setImportStatus('error');
        
        // Auto-retry connection after a delay if import is still active
        if (activeImportRef.current && !importCompletedRef.current) {
          setTimeout(() => {
            console.log('📡 Retrying SSE connection...');
            connectToImportStream(franchiseeId, importType, progressCallback, newRecordsCallback);
          }, 3000);
        }
      };

    } catch (error) {
      console.error('📡 Failed to create SSE connection:', error);
      setConnectionError(error.message);
      setIsConnected(false);
      setImportStatus('error');
    }
  }, []);

  /**
   * Disconnect from SSE stream
   */
  const disconnectFromImportStream = useCallback(() => {
    if (eventSourceRef.current) {
      console.log('📡 Disconnecting from SSE stream');
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    
    activeImportRef.current = null;
    importCompletedRef.current = false;
    setIsConnected(false);
    setImportStatus(null);
    setNewRecords([]);
    setImportProgress({ current: 0, total: 0 });
  }, []);

  /**
   * Clear new records (called after UI updates)
   */
  const clearNewRecords = useCallback(() => {
    setNewRecords([]);
  }, []);

  /**
   * Enhanced progress callback that works with existing import functions
   * Maintains backward compatibility while adding SSE capabilities
   */
  const createEnhancedProgressCallback = useCallback((originalCallback) => {
    return (current, total, newRecordsArray = []) => {
      // Call original callback for existing progress UI
      if (originalCallback) {
        originalCallback(current, total, newRecordsArray);
      }
      
      // Update local state for SSE integration
      setImportProgress({ current, total });
      
      // Handle any records passed via traditional callback
      if (newRecordsArray && newRecordsArray.length > 0) {
        setNewRecords(prev => [...prev, ...newRecordsArray]);
        
        // Call new records callback if available
        if (newRecordsCallbackRef.current) {
          newRecordsCallbackRef.current(newRecordsArray, { current, total });
        }
      }
    };
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      disconnectFromImportStream();
    };
  }, [disconnectFromImportStream]);

  return {
    // Connection state
    isConnected,
    connectionError,
    importStatus,
    
    // Real-time data
    newRecords,
    importProgress,
    
    // Control functions
    connectToImportStream,
    disconnectFromImportStream,
    clearNewRecords,
    createEnhancedProgressCallback,
    
    // Helper to check if currently importing
    isImporting: importStatus === 'importing',
    isCompleted: importStatus === 'completed',
    hasError: importStatus === 'error' || !!connectionError
  };
};

export default useImportStream;